// Characters that ALM field-mapping values must not contain. Centralised
// because three call-sites (JiraSource, JiraFieldList, FieldMappingForm)
// share this rule and previously each declared their own regex.
const PROHIBITED_FIELD_CHARS = /[[\]{}"]/g;

/** Returns the deduped set of prohibited characters present in `value`; coerces non-strings. */
export function findProhibitedFieldChars(value) {
  return [...new Set(String(value ?? "").match(PROHIBITED_FIELD_CHARS) ?? [])];
}

/** Returns `true` if `value` contains any prohibited field characters. */
export function hasProhibitedFieldChars(value) {
  return findProhibitedFieldChars(value).length > 0;
}

/** `ValidatedInput`-shaped validator: returns `""` when `value` is valid JSON (or empty), otherwise an error message. */
export function validateJsonString(value) {
  if (!value) return "";
  try {
    JSON.parse(value);
    return "";
  } catch {
    return "Value must be a valid JSON string.";
  }
}

export function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

export function validateUrl(url) {
  const re =
    /http(s|):\/\/([A-z\d]+:.+@|)[A-z\d][A-z\d.-]+\.[A-z\d]+(:(6553[0-5]|655[0-2]\d|65[0-4]\d{2}|6[0-4]\d{3}|[1-5]\d{4}|[1-9]\d{0,3})|)(\/[A-z\d./:?=_\-%&#]+|)?/gi;
  return re.test(url);
}

export function getPasswordRuleResults(
  password,
  {
    minLength = 8,
    maxLength = 256,
    requireMixedCase = true,
    requireNonAlpha = true,
    requireNumber = true,
  } = {},
) {
  const rules = [];

  rules.push({
    rule: `At least ${minLength} characters`,
    passed: password.length >= minLength,
  });

  if (maxLength) {
    rules.push({
      rule: `No more than ${maxLength} characters`,
      passed: password.length <= maxLength,
    });
  }

  if (requireMixedCase) {
    rules.push({
      rule: "At least one uppercase character",
      passed: hasUpperCase(password),
    });
    rules.push({
      rule: "At least one lowercase character",
      passed: hasLowerCase(password),
    });
  }

  if (requireNonAlpha) {
    rules.push({
      rule: "At least one symbol character (*-_$@)",
      passed: hasSpecialCharacter(password),
    });
  }

  if (requireNumber) {
    rules.push({ rule: "At least one number", passed: hasNumber(password) });
  }

  return rules;
}

export function validatePasswordByRules(password, options = {}) {
  const results = getPasswordRuleResults(password, options);
  const failed = results.find((r) => !r.passed);
  return failed ? `Password must contain: ${failed.rule.toLowerCase()}` : undefined;
}

function hasLowerCase(str) {
  return str.toUpperCase() !== str;
}

function hasUpperCase(str) {
  return str.toLowerCase() !== str;
}

function hasSpecialCharacter(str) {
  const re = new RegExp("^[0-9A-Za-zÀ-ÿ]+$");
  return !re.test(str);
}

function hasNumber(str) {
  const re = new RegExp("\\d");
  return re.test(str);
}
